import { normalizeStep } from '@/pages/ipe/workbench/constants/ipeWorkflowStorage';

export const WORKFLOW_START_NODE_ID = '__start__';
export const WORKFLOW_END_NODE_ID = '__end__';

export const DEFAULT_NODE_SIZE = { w: 220, h: 72 };
export const DEFAULT_START_SIZE = { w: 96, h: 40 };
export const DEFAULT_END_SIZE = { w: 96, h: 40 };
export const MIN_TERMINAL_W = 72;
export const MIN_TERMINAL_H = 36;
export const MIN_NODE_W = 140;
export const MIN_NODE_H = 48;

export function createNodeId() {
  return `wf-node-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function createEdgeId(from, to) {
  return `wf-edge-${from}-${to}-${Math.random().toString(36).slice(2, 6)}`;
}

/** 新建模板时的默认画布：开始节点 + 结束节点 */
export function createDefaultCanvas() {
  return {
    start: { x: 280, y: 80, w: DEFAULT_START_SIZE.w, h: DEFAULT_START_SIZE.h },
    end: { x: 280, y: 320, w: DEFAULT_END_SIZE.w, h: DEFAULT_END_SIZE.h },
    nodes: [],
    edges: [],
  };
}

function ensureEndNode(canvas) {
  if (canvas.end && typeof canvas.end === 'object') return canvas;
  return {
    ...canvas,
    end: { x: 280, y: 320, w: DEFAULT_END_SIZE.w, h: DEFAULT_END_SIZE.h },
  };
}

/** 将线性 steps 迁移为画布布局（兼容旧模板） */
export function stepsToCanvas(steps) {
  const canvas = createDefaultCanvas();
  if (!Array.isArray(steps) || steps.length === 0) return canvas;

  let prevId = WORKFLOW_START_NODE_ID;
  let y = canvas.start.y + canvas.start.h + 80;
  const x = canvas.start.x - (DEFAULT_NODE_SIZE.w - canvas.start.w) / 2;

  steps.forEach((step) => {
    const normalized = normalizeStep(step);
    const nodeId = step.id || createNodeId();
    canvas.nodes.push({
      id: nodeId,
      navId: normalized.navId,
      label: normalized.label,
      onFailure: normalized.onFailure,
      params: normalized.params,
      x,
      y,
      w: DEFAULT_NODE_SIZE.w,
      h: DEFAULT_NODE_SIZE.h,
    });
    canvas.edges.push({
      id: createEdgeId(prevId, nodeId),
      from: prevId,
      to: nodeId,
    });
    prevId = nodeId;
    y += DEFAULT_NODE_SIZE.h + 64;
  });

  canvas.end = {
    x: canvas.start.x,
    y: y + 16,
    w: DEFAULT_END_SIZE.w,
    h: DEFAULT_END_SIZE.h,
  };
  canvas.edges.push({
    id: createEdgeId(prevId, WORKFLOW_END_NODE_ID),
    from: prevId,
    to: WORKFLOW_END_NODE_ID,
  });

  return canvas;
}

/** 从已有 canvas 或 steps 解析画布状态 */
export function resolveCanvasFromTemplate(template) {
  if (template?.canvas && typeof template.canvas === 'object') {
    const c = template.canvas;
    const resolved = ensureEndNode({
      start: {
        x: Number(c.start?.x) || 280,
        y: Number(c.start?.y) || 80,
        w: Number(c.start?.w) || DEFAULT_START_SIZE.w,
        h: Number(c.start?.h) || DEFAULT_START_SIZE.h,
      },
      end: c.end
        ? {
            x: Number(c.end?.x) || 280,
            y: Number(c.end?.y) || 320,
            w: Number(c.end?.w) || DEFAULT_END_SIZE.w,
            h: Number(c.end?.h) || DEFAULT_END_SIZE.h,
          }
        : { x: 280, y: 320, w: DEFAULT_END_SIZE.w, h: DEFAULT_END_SIZE.h },
      nodes: Array.isArray(c.nodes)
        ? c.nodes.map((n) => {
            const step = normalizeStep(n);
            return {
              id: n.id || createNodeId(),
              navId: step.navId,
              label: n.label || step.label || '',
              onFailure: step.onFailure,
              params: step.params,
              x: Number(n.x) || 0,
              y: Number(n.y) || 0,
              w: Math.max(MIN_NODE_W, Number(n.w) || DEFAULT_NODE_SIZE.w),
              h: Math.max(MIN_NODE_H, Number(n.h) || DEFAULT_NODE_SIZE.h),
            };
          })
        : [],
      edges: Array.isArray(c.edges)
        ? c.edges
            .filter((e) => e?.from && e?.to)
            .map((e) => ({ id: e.id || createEdgeId(e.from, e.to), from: e.from, to: e.to }))
        : [],
    });
    return resolved;
  }
  return stepsToCanvas(template?.steps || []);
}

function nodeSortKey(id, start, end, nodeMap) {
  if (id === WORKFLOW_START_NODE_ID) return { y: start.y, x: start.x };
  if (id === WORKFLOW_END_NODE_ID) return { y: end.y, x: end.x };
  const n = nodeMap.get(id);
  return { y: n?.y ?? 0, x: n?.x ?? 0 };
}

/** 结束节点是否已接入流程（从「开始」可达且至少有一条边连入「结束」） */
export function isEndNodeConnected(canvas) {
  const edges = canvas?.edges || [];
  const hasIncomingToEnd = edges.some((e) => e.to === WORKFLOW_END_NODE_ID);
  if (!hasIncomingToEnd) return false;

  const adj = new Map();
  edges.forEach((e) => {
    if (!e.from || !e.to) return;
    if (!adj.has(e.from)) adj.set(e.from, []);
    adj.get(e.from).push(e.to);
  });

  const visited = new Set();
  const stack = [WORKFLOW_START_NODE_ID];
  while (stack.length) {
    const id = stack.pop();
    if (id === WORKFLOW_END_NODE_ID) return true;
    if (visited.has(id)) continue;
    visited.add(id);
    (adj.get(id) || []).forEach((nextId) => stack.push(nextId));
  }
  return false;
}

function sortNodesByPosition(nodeIds, nodeMap, start, end) {
  return [...nodeIds].sort((a, b) => {
    const ka = nodeSortKey(a, start, end, nodeMap);
    const kb = nodeSortKey(b, start, end, nodeMap);
    return ka.y - kb.y || ka.x - kb.x;
  });
}

function nodeToStep(node) {
  if (!node?.navId) return null;
  return normalizeStep({
    id: node.id,
    navId: node.navId,
    label: node.label,
    onFailure: node.onFailure,
    params: node.params,
  });
}

/** 从「开始」节点沿连线可达的功能节点 id 集合（未连线的节点不参与执行） */
export function resolveReachableFlowNodeIds(canvas) {
  const nodes = (canvas?.nodes || []).filter((n) => n?.navId);
  const edges = canvas?.edges || [];
  const adj = new Map();
  edges.forEach((e) => {
    if (!e.from || !e.to) return;
    if (!adj.has(e.from)) adj.set(e.from, []);
    adj.get(e.from).push(e.to);
  });
  const reachable = new Set();
  const stack = [WORKFLOW_START_NODE_ID];
  while (stack.length) {
    const id = stack.pop();
    if (reachable.has(id)) continue;
    reachable.add(id);
    (adj.get(id) || []).forEach((nextId) => stack.push(nextId));
  }
  return new Set(nodes.filter((n) => reachable.has(n.id)).map((n) => n.id));
}

/** 未接入流程（从「开始」不可达）的功能节点，保存前需提示用户连线或删除 */
export function listUnreachableCanvasNodes(canvas) {
  const nodes = (canvas?.nodes || []).filter((n) => n?.navId);
  const reachable = resolveReachableFlowNodeIds(canvas);
  return nodes.filter((n) => !reachable.has(n.id));
}

/**
 * 按 DAG 拓扑分层：同一层节点可并行执行（如开始分叉出的多条支路）
 * @returns {Array<Array<object>>} 不含开始/结束节点
 */
export function canvasToStepStages(canvas) {
  // 仅执行从「开始」沿连线可达的节点，未连线（孤立）的节点一律忽略
  const reachableIds = resolveReachableFlowNodeIds(canvas);
  const nodes = (canvas?.nodes || []).filter((n) => n?.navId && reachableIds.has(n.id));
  const edges = canvas?.edges || [];
  const start = canvas?.start || createDefaultCanvas().start;
  const end = canvas?.end || createDefaultCanvas().end;
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const preds = new Map(nodes.map((n) => [n.id, []]));

  edges.forEach((e) => {
    if (!e.from || !e.to || e.to === WORKFLOW_END_NODE_ID) return;
    if (!nodeMap.has(e.to)) return;
    if (e.from === WORKFLOW_START_NODE_ID) {
      preds.get(e.to).push(WORKFLOW_START_NODE_ID);
      return;
    }
    if (nodeMap.has(e.from)) {
      preds.get(e.to).push(e.from);
    }
  });

  const layer = new Map([[WORKFLOW_START_NODE_ID, -1]]);
  const nodeIds = nodes.map((n) => n.id);
  let changed = true;
  while (changed) {
    changed = false;
    for (const id of nodeIds) {
      const ps = preds.get(id) || [];
      const maxPred = ps.length === 0
        ? layer.get(WORKFLOW_START_NODE_ID)
        : Math.max(...ps.map((p) => layer.get(p) ?? -1));
      const nextLayer = maxPred + 1;
      if ((layer.get(id) ?? -1) < nextLayer) {
        layer.set(id, nextLayer);
        changed = true;
      }
    }
  }

  const byLayer = new Map();
  nodeIds.forEach((id) => {
    const l = layer.get(id);
    if (l == null || l < 0) return;
    if (!byLayer.has(l)) byLayer.set(l, []);
    byLayer.get(l).push(id);
  });

  const stages = [...byLayer.keys()]
    .sort((a, b) => a - b)
    .map((l) =>
      sortNodesByPosition(byLayer.get(l), nodeMap, start, end)
        .map((id) => nodeToStep(nodeMap.get(id)))
        .filter(Boolean),
    )
    .filter((stage) => stage.length > 0);

  return stages;
}

/** 将分层步骤展平为线性 steps（兼容旧逻辑） */
export function flattenStepStages(stages) {
  if (!Array.isArray(stages)) return [];
  return stages.flat();
}

/** 按连线拓扑生成运行时 steps（展平后的线性列表，不含开始/结束节点） */
export function canvasToSteps(canvas) {
  return flattenStepStages(canvasToStepStages(canvas));
}

function stepIdentityKey(step) {
  if (!step) return '';
  if (step.id) return `id:${step.id}`;
  return `${step.navId || ''}|${step.label || ''}`;
}

/**
 * 枚举从「开始」到「结束」的所有分支路径（保留前后依赖）
 * 例：开始→模特试衣→姿势裂变→结束 与 开始→AI文案→结束
 * 得到 [[模特试衣, 姿势裂变], [AI文案]]
 */
export function canvasToBranchPaths(canvas) {
  const nodes = (canvas?.nodes || []).filter((n) => n?.navId);
  const edges = canvas?.edges || [];
  const start = canvas?.start || createDefaultCanvas().start;
  const end = canvas?.end || createDefaultCanvas().end;
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const adj = new Map();

  edges.forEach((e) => {
    if (!e.from || !e.to) return;
    if (!adj.has(e.from)) adj.set(e.from, []);
    adj.get(e.from).push(e.to);
  });

  adj.forEach((succs, from) => {
    adj.set(from, sortNodesByPosition(succs, nodeMap, start, end));
  });

  const paths = [];
  const MAX_PATHS = 16;

  const dfs = (nodeId, acc, visiting) => {
    if (paths.length >= MAX_PATHS) return;
    if (nodeId === WORKFLOW_END_NODE_ID) {
      if (acc.length > 0) paths.push([...acc]);
      return;
    }
    if (visiting.has(nodeId)) return;

    let nextAcc = acc;
    if (nodeId !== WORKFLOW_START_NODE_ID && nodeMap.has(nodeId)) {
      const step = nodeToStep(nodeMap.get(nodeId));
      if (step) nextAcc = [...acc, step];
    }

    const succs = adj.get(nodeId) || [];
    if (succs.length === 0) {
      if (nextAcc.length > 0) paths.push([...nextAcc]);
      return;
    }

    const nextVisiting = new Set(visiting);
    nextVisiting.add(nodeId);
    succs.forEach((nextId) => dfs(nextId, nextAcc, nextVisiting));
  };

  dfs(WORKFLOW_START_NODE_ID, [], new Set());

  const seen = new Set();
  return paths.filter((path) => {
    const key = path.map(stepIdentityKey).join('\0');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function getPortPosition(node, port, canvasMeta) {
  const startNode = canvasMeta?.start;
  const endNode = canvasMeta?.end;

  if (node.id === WORKFLOW_START_NODE_ID) {
    const s = startNode || node;
    if (port === 'out') return { x: s.x + s.w, y: s.y + s.h / 2 };
    return { x: s.x, y: s.y + s.h / 2 };
  }

  if (node.id === WORKFLOW_END_NODE_ID) {
    const e = endNode || node;
    if (port === 'in') return { x: e.x, y: e.y + e.h / 2 };
    return { x: e.x + e.w, y: e.y + e.h / 2 };
  }

  if (port === 'out') return { x: node.x + node.w, y: node.y + node.h / 2 };
  return { x: node.x, y: node.y + node.h / 2 };
}

export function computeCanvasBounds(canvas, padding = 120) {
  const end = canvas.end || createDefaultCanvas().end;
  const items = [
    { x: canvas.start.x, y: canvas.start.y, w: canvas.start.w, h: canvas.start.h },
    { x: end.x, y: end.y, w: end.w, h: end.h },
    ...canvas.nodes,
  ];
  if (items.length === 0) {
    return { width: 1200, height: 800, minX: 0, minY: 0 };
  }
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  items.forEach((n) => {
    minX = Math.min(minX, n.x);
    minY = Math.min(minY, n.y);
    maxX = Math.max(maxX, n.x + n.w);
    maxY = Math.max(maxY, n.y + n.h);
  });
  return {
    minX: Math.max(0, minX - padding),
    minY: Math.max(0, minY - padding),
    width: Math.max(1200, maxX - minX + padding * 2),
    height: Math.max(800, maxY - minY + padding * 2),
  };
}

export function isTerminalNodeId(nodeId) {
  return nodeId === WORKFLOW_START_NODE_ID || nodeId === WORKFLOW_END_NODE_ID;
}
